import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";

interface ModalVisual {
  imageUrl: string;
  imageAlt: string;
  eyebrow?: string;
  meta?: string;
}
interface ConfirmOptions { title: string; message: string; confirmLabel?: string; cancelLabel?: string; danger?: boolean; visual?: ModalVisual }
interface PromptOptions extends ConfirmOptions { defaultValue?: string; placeholder?: string; multiline?: boolean }
interface ModalApi {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
  custom: (options: { title: string; content: (close: () => void) => ReactNode; className?: string }) => Promise<void>;
}
type Request =
  | ({ kind: "confirm" } & ConfirmOptions & { resolve: (value: boolean) => void })
  | ({ kind: "prompt" } & PromptOptions & { resolve: (value: string | null) => void })
  | ({ kind: "custom"; title: string; content: (close: () => void) => ReactNode; className?: string; resolve: () => void });

const ModalContext = createContext<ModalApi | null>(null);

export function useAppModal() {
  const value = useContext(ModalContext);
  if (!value) throw new Error("useAppModal must be used inside AppModalProvider");
  return value;
}

export function AppModalProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const requestRef = useRef<Request | null>(null);
  const settle = useCallback((value: boolean | string | null) => {
    const current = requestRef.current;
    if (!current) return;
    requestRef.current = null;
    setRequest(null);
    if (current.kind === "confirm") current.resolve(Boolean(value));
    else if (current.kind === "prompt") current.resolve(typeof value === "string" ? value : null);
    else current.resolve();
  }, []);
  const open = useCallback((next: Request) => {
    const previous = requestRef.current;
    if (previous?.kind === "confirm") previous.resolve(false);
    else if (previous?.kind === "prompt") previous.resolve(null);
    else if (previous) previous.resolve();
    requestRef.current = next;
    setRequest(next);
  }, []);
  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => open({ kind: "confirm", ...options, resolve })), [open]);
  const prompt = useCallback((options: PromptOptions) => new Promise<string | null>((resolve) => {
    setInput(options.defaultValue ?? "");
    open({ kind: "prompt", ...options, resolve });
  }), [open]);
  const custom = useCallback((options: { title: string; content: (close: () => void) => ReactNode; className?: string }) => new Promise<void>((resolve) => open({ kind: "custom", ...options, resolve })), [open]);
  useEffect(() => {
    if (!request) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") settle(null); };
    document.addEventListener("keydown", onKey);
    if (request.kind === "prompt") window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => document.removeEventListener("keydown", onKey);
  }, [request, settle]);
  return <ModalContext.Provider value={{ confirm, prompt, custom }}>
    {children}
    {request && <div className={`modal-backdrop app-modal-backdrop ${"visual" in request && request.visual ? "app-modal-visual-backdrop" : ""}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) settle(null); }}>
      <section className={`modal app-modal ${"visual" in request && request.visual ? "app-modal-visual" : ""} ${request.kind === "custom" ? request.className ?? "app-modal-custom" : ""}`} role="dialog" aria-modal="true" aria-labelledby="app-modal-title">
        {request.kind === "custom" ? <><header><h2 id="app-modal-title">{request.title}</h2><button className="icon-button" aria-label="关闭" onClick={() => settle(null)}><X /></button></header>{request.content(() => settle(null))}</> : request.visual ? <>
          <div className="app-modal-visual-art" role="img" aria-label={request.visual.imageAlt} style={{ backgroundImage: `url(${request.visual.imageUrl})` }} />
          <button className="icon-button app-modal-visual-close" aria-label="关闭" onClick={() => settle(null)}><X /></button>
          <div className="app-modal-visual-copy">
            <span className="eyebrow">{request.visual.eyebrow ?? "请确认"}</span>
            <h2 id="app-modal-title">{request.title}</h2>
            <p>{request.message}</p>
            {request.visual.meta && <small>{request.visual.meta}</small>}
            <footer><button onClick={() => settle(null)}>{request.cancelLabel ?? "取消"}</button><button className={request.danger ? "danger" : "primary"} onClick={() => settle(true)}>{request.confirmLabel ?? "确认"}</button></footer>
          </div>
        </> : <>
          <header><div><span className="eyebrow">请确认</span><h2 id="app-modal-title">{request.title}</h2></div><button className="icon-button" aria-label="关闭" onClick={() => settle(null)}><X /></button></header>
          <p>{request.message}</p>
          {request.kind === "prompt" && (request.multiline ? <textarea ref={inputRef as React.RefObject<HTMLTextAreaElement>} value={input} placeholder={request.placeholder} onChange={(event) => setInput(event.target.value)} /> : <input ref={inputRef as React.RefObject<HTMLInputElement>} value={input} placeholder={request.placeholder} onChange={(event) => setInput(event.target.value)} />)}
          <footer><button onClick={() => settle(null)}>{request.cancelLabel ?? "取消"}</button><button className={request.danger ? "danger" : "primary"} disabled={request.kind === "prompt" && !input.trim()} onClick={() => settle(request.kind === "prompt" ? input.trim() : true)}>{request.confirmLabel ?? "确认"}</button></footer>
        </>}
      </section>
    </div>}
  </ModalContext.Provider>;
}
